import { DeliveryApi } from '@m4a3/api-client/dist/delivery-api';
import { OrderfulApi } from '@m4a3/api-client/dist/orderful-api';
import { OpeningHour } from '@m4a3/api-client/dist/models/shared/opening-hours.interface';
import { API, Auth } from 'aws-amplify';
import { selectSelectedCellId } from 'selectors/ordersSelector';
import { getEnvironment } from '../services/environment';
import {
  GET_PARTNERS_FAILURE,
  GET_PARTNERS_REQUEST,
  GET_PARTNERS_SUCCESS,
  GET_POINTOFSALE_REQUEST,
  GET_POINTOFSALE_SUCCESS,
  GET_POINTOFSALE_FAILURE,
  GET_REGIONS_FAILURE,
  GET_REGIONS_REQUEST,
  GET_REGIONS_SUCCESS,
  ADD_POINTOFSALE_REQUEST,
  ADD_PARTNER_REQUEST,
  UPLOAD_IMAGE_REQUEST,
  UPDATE_POINTOFSALE_REQUEST,
  UPDATE_POINTOFSALE_SUCCESS,
  UPDATE_POINTOFSALE_FAILURE,
  ADD_POINTOFSALE_SUCCESS,
  ADD_POINTOFSALE_FAILURE,
  SET_SELECTED_REGION,
  UPDATE_OPENING_HOURS_REQUEST,
  UPDATE_OPENING_HOURS_SUCCESS,
  UPDATE_OPENING_HOURS_FAILURE,
  REROUTE_PENDING,
  REROUTE_FULFILLED,
  REROUTE_FAILURE,
  FLIPDISH_MENU_SYNC_FAILED,
  FLIPDISH_MENU_SYNC_FULFILLED,
  FLIPDISH_MENU_SYNC_PENDING,
  MESSAGING_PENDING,
  SOCKET_ERROR,
  MESSAGING_FAILED,
} from './actionTypes';
import { newNotification } from './uiActions';
import { getLatestJwtToken } from '../services/amplify-auth';
import { logToDatadog } from '../services/datadog';
import { deliveryApi, orderfulApi } from '../services/api-instances';
import Sockette from 'sockette';

export const getPartners = () => async dispatch => {
  try {
    dispatch({ type: GET_PARTNERS_REQUEST });
    const partners = await orderfulApi.getAllPartners();
    dispatch({
      type: GET_PARTNERS_SUCCESS,
      payload: partners
    });
  } catch (error) {
    logToDatadog('error_getting_partners', {error}, 'error');
    dispatch({ type: GET_PARTNERS_FAILURE });
    dispatch(
      newNotification({
        variant: 'error',
        message: 'Error getting partners'
      })
    );
  }
};

export const getRegions = () => async dispatch => {
  try {
    dispatch({ type: GET_REGIONS_REQUEST });
    const regions = await orderfulApi.getAllRegions();
    dispatch({
      type: GET_REGIONS_SUCCESS,
      payload: regions
    });
  } catch (error) {
    logToDatadog('error_getting_regions', {error}, 'error');
    dispatch({ type: GET_REGIONS_FAILURE });
    dispatch(
      newNotification({
        variant: 'error',
        message: 'Error getting regions'
      })
    );
  }
};

export const getPointOfSales = cellId => async dispatch => {
  try {
    dispatch({ type: GET_POINTOFSALE_REQUEST });
    const pointOfSales = await orderfulApi.getAllPointOfSalesForCell(cellId, { openingHours: false })
    dispatch({
      type: GET_POINTOFSALE_SUCCESS,
      payload: pointOfSales
    });
  } catch (error) {
    logToDatadog('error_getting_point_of_sale', {error}, 'error');
    dispatch({ type: GET_POINTOFSALE_FAILURE });
    dispatch(
      newNotification({
        variant: 'error',
        message: 'Error getting point of sales'
      })
    );
  }
};


let messagingSocket;
const fiveSecondsInMiliseconds = 5000;
export const subscribeToMessaging = () => async dispatch => {
  dispatch({ type: MESSAGING_PENDING });
  try {
    if (messagingSocket) {
      messagingSocket.close();
      messagingSocket = null;
    }

    const merchantUrl = `${process.env.REACT_APP_MESSAGING_URL}?client=Merchants`;

    messagingSocket = new Sockette(merchantUrl, {
      timeout: fiveSecondsInMiliseconds,
      maxAttempts: 10,
      onopen: evt => {
        console.log('merchant socket open');
      },
      onmessage: message => {
        dispatch(consumeMannaMessage(message?.data));
      },
      onreconnect: () => {
        console.log('merchant socket reconnect');
      },
      onclose: e => {},
      onerror: err => {
        dispatch({ type: SOCKET_ERROR, payload: err });
        newNotification({
          variant: 'error',
          message: 'Socket Error.'
        })
      }
    });
  } catch (err) {
    dispatch({ type: MESSAGING_FAILED, payload: err });
    newNotification({
      variant: 'error',
      message: 'Messaging Error with Socket.'
    })
  }
};

const consumeMannaMessage = message => dispatch => {
  try {
    const data = JSON.parse(message);

    if (data?.type === 'updateMerchant') {
      dispatch(updatePointOfSale(data));
    }
  } catch (err) {}
};

export const updateMerchant = (merchant) => {
  if (messagingSocket) {
    messagingSocket.send(
      JSON.stringify({
        type: 'updateMerchant',
        data: merchant
      })
    );
  }
};

export const updatePointOfSale = pointOfSale => async (dispatch, getState) => {

  dispatch({ type: UPDATE_POINTOFSALE_REQUEST });
  try {
    const currentState = getState();
    await orderfulApi.updateMerchant(pointOfSale, currentState.orders.selectedCellId, pointOfSale.id);
    dispatch(getPointOfSales(currentState.orders.selectedCellId));
    dispatch({ type: UPDATE_POINTOFSALE_SUCCESS });
    dispatch(
      newNotification({
        variant: 'success',
        message: 'Updated point of sale successfully'
      })
    );
    reloadCache();
  } catch (error) {
    dispatch({ type: UPDATE_POINTOFSALE_FAILURE });
    logToDatadog('error_updating_point_of_sale', {error, pointOfSale}, 'error');
    dispatch(
      newNotification({
        variant: 'error',
        message: 'Error updating point of sale'
      })
    );
  }
};

export const addPointOfSale = pointOfSale => async (dispatch, getState) => {

  dispatch({ type: ADD_POINTOFSALE_REQUEST });
  try {
    await orderfulApi.addPointOfSale(pointOfSale);
    await reloadCache();
    const currentState = getState();
    dispatch({ type: ADD_POINTOFSALE_SUCCESS });
    dispatch(getPointOfSales(currentState.orders.selectedCellId));
    dispatch(
      newNotification({
        variant: 'success',
        message: 'Added point of sale successfully'
      })
    );
  } catch (error) {
    dispatch({ type: ADD_POINTOFSALE_FAILURE });
    logToDatadog('error_adding_point_of_sale', {error, pointOfSale}, 'error');
    dispatch(
      newNotification({
        variant: 'error',
        message: 'Error adding point of sale'
      })
    );
  }
};

export const updateOpeningHours = (openingHours: OpeningHour[]) => async (
  dispatch,
  getState
) => {

  dispatch({ type: UPDATE_OPENING_HOURS_REQUEST });
  try {
    await orderfulApi.updateOpeningHoursForPointOfSale(openingHours)
    await reloadCache();
    dispatch({ type: UPDATE_OPENING_HOURS_SUCCESS });
    const currentState = getState();
    dispatch(getPointOfSales(currentState.orders.selectedCellId));
    dispatch(
      newNotification({
        variant: 'success',
        message: 'Updated opening hours'
      })
    );
  } catch (error) {
    dispatch({ type: UPDATE_OPENING_HOURS_FAILURE });
    dispatch(
      newNotification({
        variant: 'error',
        message: 'Failed to update opening hours'
      })
    );
  }
};

export const sleep = (ms: number): Promise<null> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(null);
    }, ms);
  });
};

export const reloadCache = () => {
  return orderfulApi.reloadCache();
};

export const rerouteThunk = () => async (dispatch, getState) => {
  try {
    dispatch({ type: REROUTE_PENDING });
    const selectedCellId = selectSelectedCellId(getState());
    await API.post('api', `/mission/route/reroute/${selectedCellId}`, {});

    dispatch({
      type: REROUTE_FULFILLED,
      meta: { success: { message: 'Reroute succeeded' } }
    });
  } catch (error) {
    logToDatadog('mission_reroute_failure', {e: JSON.stringify({ type: REROUTE_FAILURE, meta: { error } })});
    dispatch({ type: REROUTE_FAILURE, meta: { error } });
  }
};

export const rerouteOrderThunk = (orderId: string) => async (dispatch, getState) => {
  try {
    dispatch({ type: REROUTE_PENDING });
    const selectedCellId = selectSelectedCellId(getState());
    await API.post(
      'api',
      `/mission/route/reroute/${selectedCellId}?orderId=${orderId}`,
      {}
    );

    dispatch({
      type: REROUTE_FULFILLED,
      meta: { success: { message: 'Reroute succeeded' } }
    });
  } catch (error) {
    logToDatadog('mission_reroute_failure', {e: JSON.stringify({ type: REROUTE_FAILURE, meta: { error } })});
    dispatch({ type: REROUTE_FAILURE, meta: { error } });
  }
};
export const syncFlipdishMenu = (posId: number) => async (dispatch) => {
  try {
    dispatch({ type: FLIPDISH_MENU_SYNC_PENDING });
    await orderfulApi.syncFlipdishMenu(posId);

    dispatch({
      type: FLIPDISH_MENU_SYNC_FULFILLED,
      meta: { success: { message: 'Menu sync successful' } }
    });
  } catch (error) {
    dispatch({ type: FLIPDISH_MENU_SYNC_FAILED, meta: { error } });
  }
};

export const addPartner = partner => async (dispatch, getState) => {
  try {
    dispatch({ type: ADD_PARTNER_REQUEST });
    await orderfulApi.createPartner(partner);
    await reloadCache();
    const currentState = getState();
  //  dispatch(getPartners());
    dispatch(
      newNotification({
        variant: 'success',
        message: 'Added partner successfully'
      })
    );
  } catch (error) {
    logToDatadog('error_adding_partner', {error, partner}, 'error');
    dispatch(
      newNotification({
        variant: 'error',
        message: 'Error adding partners'
      })
    );
  }
};

export const validateDropLocation = async (
  lat: number, 
  lng: number
  ): Promise<string> => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_ADMIN_URL}/drop-location/evaluateRisk/${lat}/${lng}`,
      {
        headers: {
          Authorization: `Bearer ${await getLatestJwtToken()}`
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.text();
    return data;
  } catch (error) {
    console.error('Error validating drop location:', error);
    logToDatadog('drop_location_validation_error', { 
      lat, 
      lng,
      error: error.message 
    });
  }
};
